import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { defineMessages, useIntl } from 'react-intl';
import { MediaCardProps } from 'src/apps/Onboarding/Bienvenue/MediaCard';
import { range } from 'src/utils/range';
import { LanguageApi, PathApi } from 'src/api';
import { useQuery } from '@tanstack/react-query';
import { PathOverviewResponse } from 'src/api/path/types';
import { getLanguageImage } from 'src/utils/language/getLanguageImage';
import { saveSourceLanguage } from 'src/utils/language/saveLastLanguagePair';
import { ErrorModal } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { SourceLanguagesResponse } from 'src/api/language/types';
import { ONE_MINUTE } from 'src/constants/times.const';
import { redirectLocation } from 'src/utils/location';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import { getLanguageVideo } from '../videos';
import * as S from './styles';
import { getPreselectedSourceLanguage } from './getPreselectedSourceLanguage';

const msgs = defineMessages({
  iSpeakPreInput: {
    id: 'onboarding_chooseLanguage_ISpeak_preInput',
  },
  iSpeakPostInput: {
    id: 'onboarding_chooseLanguage_ISpeak_postInput',
  },
  errorTitle: {
    id: 'payment_page_ccform_error_othererror',
  },
});

// Import the MediaCard dynamically to:
// 1) disable SSR to avoid initially rendering for desktop on mobile
// 2) Small performance gain
const SMediaCard = dynamic(
  // There is some strange type error here that I can't
  // understand. The type-casting gets around it fine,
  // however.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  () => import('../styles').then(mod => mod.MediaCard as any),
) as React.ComponentType<MediaCardProps>;

const ChooseLanguage = ({ nextUrl }: { nextUrl?: string }) => {
  const intl = useIntl();
  const router = useRouter();
  const { next } = router.query;
  const { safePush } = useSafePush();
  const [sourceLanguage, setSourceLanguage] = useState(getPreselectedSourceLanguage(intl, router));

  const { data: sourceLanguages, error: languagesError } = useQuery<SourceLanguagesResponse>({
    queryKey: ['sourceLanguages'],
    queryFn: () => {
      return LanguageApi.getSourceLanguages();
    },
    staleTime: ONE_MINUTE,
  });

  const {
    data: pathsOverview,
    refetch: refetchPathsOverview,
    isLoading,
    error: pathsError,
  } = useQuery<PathOverviewResponse>({
    queryKey: ['pathsOverview', sourceLanguage],
    queryFn: () => {
      return PathApi.getPathsOverview({
        source_language_locale: sourceLanguage,
      });
    },
    staleTime: ONE_MINUTE,
  });

  const sourceOptions = sourceLanguages?.map(({ locale, name }) => ({
    name,
    value: locale,
  }));

  useEffect(() => {
    void refetchPathsOverview();
  }, [refetchPathsOverview, sourceLanguage]);

  /* istanbul ignore next: hard to mock error */
  if (languagesError || pathsError) {
    return (
      <ErrorModal
        isOpen
        errorTitle={intl.formatMessage(msgs.errorTitle)}
        errorDetail={languagesError?.toString() || pathsError?.toString()}
        testId="error-modal"
      />
    );
  }

  return (
    <S.SectionContainer>
      <S.SourceLanguageContainer>
        <S.SourceLanguageText>{intl.formatMessage(msgs.iSpeakPreInput)}</S.SourceLanguageText>
        {sourceOptions && (
          <S.StyledSelect
            large
            options={sourceOptions}
            selectedValue={sourceLanguage}
            handleSelect={(selectedSourceLanguage: string) => {
              setSourceLanguage(selectedSourceLanguage);
              saveSourceLanguage(selectedSourceLanguage, true);
              redirectLocation(
                stringifyUrl(ROUTES.bienvenue, next ? { next: String(next) } : null),
              );
            }}
          />
        )}
        <S.SourceLanguageText>{intl.formatMessage(msgs.iSpeakPostInput)}</S.SourceLanguageText>
      </S.SourceLanguageContainer>
      <S.CardsContainer>
        {isLoading
          ? range(8).map(n => (
              <SMediaCard key={`skeletal-${n}`} skeletal title="somelanguage" src="" />
            ))
          : pathsOverview?.paths.map(({ targetLanguageName, flagUrl, targetLanguageLocale }) => (
              <S.MediaCardContainer key={targetLanguageName}>
                <SMediaCard
                  key={targetLanguageName}
                  src={
                    getLanguageVideo(targetLanguageLocale) || getLanguageImage(targetLanguageLocale)
                  }
                  onClick={async () => {
                    const query: {
                      source: string;
                      target: string;
                      next?: string;
                    } = {
                      source: sourceLanguage,
                      target: targetLanguageLocale,
                    };
                    if (nextUrl) query.next = nextUrl;

                    await safePush({
                      pathname: ROUTES.bienvenue,
                      query,
                    });
                  }}
                  title={targetLanguageName}
                />
                <S.FlagContainer>
                  <S.FlagImg src={flagUrl} alt={targetLanguageName} data-testid="flag" />
                </S.FlagContainer>
              </S.MediaCardContainer>
            ))}
      </S.CardsContainer>
    </S.SectionContainer>
  );
};

export default ChooseLanguage;
